import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SiteNav, MinimalFooter, useReveal, useTopProgress, useStartAnfrage } from './shared';
import Seo from './Seo';

const CDN_BASE_URL = (process.env.REACT_APP_CDN_BASE_URL || 'https://hamba.fra1.cdn.digitaloceanspaces.com').replace(/\/$/, '');

const toCdnUrl = (path) => {
  if (!path || /^https?:\/\//i.test(path)) return path;
  const clean = path.replace(/^\.\//, '').replace(/^(\.\.\/)+/, '').replace(/^\/+/, '');
  return `${CDN_BASE_URL}/${clean}`;
};

const applyCdnToContent = (node) => {
  if (!React.isValidElement(node)) return node;
  const updates = {};
  if (typeof node.props.src === 'string') {
    updates.src = toCdnUrl(node.props.src);
  }
  if (node.props.children) {
    let changed = false;
    const transformed = React.Children.map(node.props.children, (child) => {
      const t = applyCdnToContent(child);
      if (t !== child) changed = true;
      return t;
    });
    if (changed) updates.children = transformed;
  }
  return Object.keys(updates).length === 0 ? node : React.cloneElement(node, updates);
};

export default function Blog({ articles }) {
  useTopProgress();
  const { slug } = useParams();
  const navigate = useNavigate();
  const startAnfrage = useStartAnfrage();
  const article = articles.find((a) => a.slug === slug);
  // Re-observe on slug change so navigating between articles refreshes the reveal observers.
  useReveal([slug]);

  // Same-origin link interception inside the article body so SPA navigation works
  useEffect(() => {
    const handler = (event) => {
      const anchor = event.target.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || /^(https?:|mailto:|tel:|#)/.test(href)) return;
      if (anchor.target === '_blank') return;
      if (href.startsWith('/')) {
        event.preventDefault();
        navigate(href);
      }
    };
    const body = document.querySelector('.blog-prose');
    body?.addEventListener('click', handler);
    return () => body?.removeEventListener('click', handler);
  }, [navigate, slug]);

  if (!article) {
    return (
      <div className="page-enter" data-screen-label="Journal · 404">
        <Seo
          path={`/blog/${slug}`}
          title="Artikel nicht gefunden — hamba"
          description="Dieser Artikel existiert nicht (mehr)."
          noindex
        />
        <SiteNav />
        <section style={{ padding: '160px 0', textAlign: 'center', minHeight: '50vh' }}>
          <div className="shell">
            <div className="section-label" style={{ justifyContent: 'center', marginBottom: 20 }}>
              <span>404</span>
            </div>
            <h1 className="h-section" style={{ marginBottom: 24 }}>Artikel nicht gefunden.</h1>
            <Link
              to="/blog"
              style={{
                fontFamily: 'var(--grotesk)',
                fontSize: 13,
                letterSpacing: '0.08em',
                color: 'var(--bordeaux)',
                textDecoration: 'none',
              }}
            >
              ← Zurück zur Übersicht
            </Link>
          </div>
        </section>
        <MinimalFooter />
      </div>
    );
  }

  const mainImage = toCdnUrl(article.mainImage);
  const authorImage = toCdnUrl(article.authorImage);
  const contentWithCdn = article.content ? applyCdnToContent(article.content) : null;

  return (
    <div className="page-enter" data-screen-label="Journal · Artikel">
      <Seo
        path={`/blog/${article.slug}`}
        title={`${article.title} — hamba`}
        description={article.description}
        image={mainImage}
        type="article"
      />
      <div id="homev2-progress" className="progress-bar" />
      <SiteNav />

      <article>
        {/* Article header */}
        <section style={{ paddingTop: 64, paddingBottom: 48 }}>
          <div className="shell" style={{ maxWidth: 880, margin: '0 auto' }}>
            <div className="reveal">
              <Link
                to="/blog"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontFamily: 'var(--grotesk)',
                  fontSize: 12,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'var(--charcoal-muted)',
                  textDecoration: 'none',
                  marginBottom: 32,
                }}
              >
                <span aria-hidden="true">←</span> Zurück zur Übersicht
              </Link>

              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  alignItems: 'center',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 11,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: 'var(--charcoal-muted)',
                  marginBottom: 24,
                }}
              >
                <span style={{ color: 'var(--bordeaux)' }}>{article.category}</span>
                <span style={{ color: 'var(--cream-line)' }}>·</span>
                <span>{article.date}</span>
              </div>

              <h1
                className="h-display"
                style={{
                  fontSize: 'clamp(34px, 4.4vw, 56px)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.1,
                  marginBottom: 28,
                  maxWidth: '24ch',
                }}
              >
                {article.title}
              </h1>

              {article.description && (
                <p
                  className="lede"
                  style={{
                    fontSize: 20,
                    fontStyle: 'italic',
                    fontFamily: 'var(--serif)',
                    color: 'var(--charcoal-muted)',
                    maxWidth: '120ch',
                    lineHeight: 1.55,
                    marginBottom: 36,
                  }}
                >
                  {article.description}
                </p>
              )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  paddingTop: 24,
                  borderTop: '1px solid var(--cream-line)',
                }}
              >
                {authorImage && (
                  <img
                    src={authorImage}
                    alt={article.author}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                )}
                <div>
                  <div
                    style={{
                      fontFamily: 'var(--grotesk)',
                      fontSize: 11,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'var(--charcoal-muted)',
                      marginBottom: 2,
                    }}
                  >
                    Autor
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--serif)',
                      fontSize: 16,
                      color: 'var(--anthracite)',
                    }}
                  >
                    {article.author}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main image */}
        {mainImage && (
          <section style={{ paddingBottom: 80 }}>
            <div className="shell" style={{ maxWidth: 1100, margin: '0 auto' }}>
              <div className="reveal" style={{
                aspectRatio: '16 / 9',
                overflow: 'hidden',
                background: 'var(--cream-deep)',
                border: '1px solid var(--cream-line)',
              }}>
                <img
                  src={mainImage}
                  alt={article.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
            </div>
          </section>
        )}

        {/* Body */}
        <section style={{ paddingBottom: 120 }}>
          <div className="shell" style={{ maxWidth: 720, margin: '0 auto' }}>
            <div className="blog-prose">
              {contentWithCdn}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={{ padding: '120px 0', background: 'var(--cream-deep)' }}>
          <div className="shell" style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
            <div className="reveal">
              <div className="section-label" style={{ justifyContent: 'center', marginBottom: 24 }}>
                <span>Bereit für den nächsten Schritt?</span>
              </div>
              <h2 className="h-section" style={{ marginBottom: 20, fontSize: 'clamp(28px, 3.2vw, 40px)' }}>
                Erstanalyse Ihres Portfolios.
              </h2>
              <p className="body-prose" style={{ color: 'var(--charcoal-muted)', marginBottom: 32, fontSize: 17 }}>
                Kostenlos, unverbindlich, vertraulich — ein persönliches Gespräch.
              </p>
              <button
                type="button"
                onClick={startAnfrage}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                  padding: '16px 32px',
                  background: 'var(--bordeaux)',
                  color: 'var(--cream)',
                  fontFamily: 'var(--grotesk)',
                  fontSize: 14,
                  letterSpacing: '0.08em',
                  fontWeight: 500,
                  border: '1px solid var(--bordeaux)',
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: 'background 200ms ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bordeaux-soft)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bordeaux)'; }}
              >
                Erstanalyse anfragen <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </section>
      </article>

      <MinimalFooter />
    </div>
  );
}
